import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Loader2, Trash2, FileDown, MessageCircle, FlaskConical } from "lucide-react";
import { TopBar, BottomNav } from "../components/Nav";
import { DiagnosisResult } from "../components/DiagnosisResult";
import { AgronomistChat } from "../components/AgronomistChat";
import { API } from "../context/AuthContext";
import { exportScanToPDF } from "../lib/pdf";
import { useLang } from "../i18n/LanguageContext";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "../components/ui/alert-dialog";

export default function ScanDetailPage() {
  const { id } = useParams();
  const { t } = useLang();
  const nav = useNavigate();
  const [scan, setScan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [exporting, setExporting] = useState(false);
  const panelRef = useRef(null);

  useEffect(() => {
    axios
      .get(`${API}/scans/${id}`)
      .then((r) => setScan(r.data))
      .catch(() => toast.error(t("scan.notFound")))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [id]);

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/scans/${id}`);
      toast.success(t("scan.deleted"));
      nav("/history");
    } catch {
      toast.error(t("scan.deleteFailed"));
    } finally {
      setDeleting(false);
    }
  };

  const onExport = async () => {
    if (!scan) return;
    setExporting(true);
    try {
      await exportScanToPDF(scan, panelRef.current);
      toast.success(t("scan.pdfExported"));
    } catch (e) {
      console.error(e);
      toast.error(t("scan.pdfFailed"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7] pb-24 md:pb-10">
      <TopBar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-6 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <Link to="/dashboard" data-testid="back-to-dashboard" className="flex items-center gap-1 text-[#1B4332] font-semibold hover:underline">
            <ArrowLeft className="w-4 h-4" /> {t("common.back")}
          </Link>
          {scan && (
            <div className="flex items-center gap-2">
              <button
                data-testid="export-pdf-btn"
                onClick={onExport}
                disabled={exporting}
                className="flex items-center gap-1.5 text-sm bg-[#F4F1EA] hover:bg-[#E5E0D8] text-[#1B4332] px-3 py-2 rounded-full font-semibold transition-colors disabled:opacity-60"
              >
                {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
                {t("scan.pdf")}
              </button>
              <button
                data-testid="chat-open-btn"
                onClick={() => setChatOpen(true)}
                className="flex items-center gap-1.5 text-sm bg-[#1B4332] text-white hover:bg-[#2D6A4F] px-3 py-2 rounded-full font-semibold transition-colors"
              >
                <MessageCircle className="w-4 h-4" /> {t("scan.askAi")}
              </button>
              <button
                data-testid="verify-medicine-btn"
                onClick={() => nav(`/chemical-check?scan=${id}`)}
                className="flex items-center gap-1.5 text-sm bg-[#F4F1EA] hover:bg-[#E5E0D8] text-[#1B4332] px-3 py-2 rounded-full font-semibold transition-colors"
              >
                <FlaskConical className="w-4 h-4" /> {t("scan.verifyMedicine")}
              </button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <button
                    data-testid="delete-scan-btn"
                    className="flex items-center gap-1.5 text-sm text-[#D90429] hover:bg-[#D90429]/10 px-3 py-2 rounded-full font-semibold transition-colors"
                  >
                    <Trash2 className="w-4 h-4" /> {t("scan.delete")}
                  </button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="delete-confirm-dialog" className="bg-white rounded-2xl border border-[#E5E0D8]">
                  <AlertDialogHeader>
                    <AlertDialogTitle className="cd-heading font-extrabold text-xl">
                      {t("scan.deleteTitle")}
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-[#2D3A35]">
                      {t("scan.deleteDesc")}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="delete-cancel-btn" className="rounded-full">
                      {t("common.cancel")}
                    </AlertDialogCancel>
                    <AlertDialogAction
                      data-testid="delete-confirm-btn"
                      onClick={confirmDelete}
                      disabled={deleting}
                      className="bg-[#D90429] hover:bg-[#a90220] text-white rounded-full"
                    >
                      {deleting ? (<><Loader2 className="w-4 h-4 animate-spin mr-1" />{t("scan.deleting")}</>) : t("common.delete")}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          )}
        </div>

        {loading && (
          <div className="flex items-center justify-center py-20 text-[#52796F]" data-testid="scan-loading">
            <Loader2 className="w-6 h-6 animate-spin" />
          </div>
        )}

        {scan && (
          <>
            <div className="rounded-3xl overflow-hidden border border-[#E5E0D8] bg-white" data-testid="scan-image-container">
              <img src={scan.image_base64} alt="Scanned crop" className="w-full max-h-96 object-cover" />
            </div>
            <div ref={panelRef}>
              <DiagnosisResult scan={scan} />
            </div>
          </>
        )}
      </main>

      {scan && (
        <AgronomistChat
          open={chatOpen}
          onClose={() => setChatOpen(false)}
          scanId={scan.id}
          language={scan.language}
        />
      )}

      <BottomNav />
    </div>
  );
}
